import React, { useState, useEffect, useRef } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { Loader2, CheckCircle, AlertCircle, CreditCard, Store } from 'lucide-react';
import { toast } from 'sonner';
import { trackAddPaymentInfo } from '../utils/fbPixel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Stripe Elements appearance customization - Alphatica brand theme (Hueso/Cobalto)
const stripeAppearance = {
  theme: 'stripe',
  variables: {
    colorPrimary: '#1B4FD8',
    colorBackground: '#FFFFFF',
    colorText: '#111118',
    colorDanger: '#DC2626',
    colorTextSecondary: 'rgba(17, 17, 24, 0.6)',
    colorTextPlaceholder: 'rgba(17, 17, 24, 0.4)',
    fontFamily: '"Inter", system-ui, sans-serif',
    spacingUnit: '4px',
    borderRadius: '8px',
    fontSizeBase: '14px',
    colorIconCardCvc: '#1B4FD8',
    colorIconCardError: '#DC2626'
  },
  rules: {
    '.Input': {
      backgroundColor: '#FFFFFF',
      border: '1px solid rgba(27, 79, 216, 0.3)',
      boxShadow: 'none',
      padding: '12px 14px',
      transition: 'border-color 0.2s ease, box-shadow 0.2s ease'
    },
    '.Input:focus': {
      borderColor: '#1B4FD8',
      boxShadow: '0 0 0 1px #1B4FD8, 0 0 12px rgba(27, 79, 216, 0.15)'
    },
    '.Input--invalid': {
      borderColor: '#DC2626'
    },
    '.Label': {
      color: 'rgba(27, 79, 216, 0.8)',
      fontSize: '11px',
      fontWeight: '500',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      marginBottom: '8px'
    },
    '.Tab': {
      backgroundColor: '#FFFFFF',
      border: '1px solid rgba(27, 79, 216, 0.3)',
      color: 'rgba(17, 17, 24, 0.7)'
    },
    '.Tab:hover': {
      borderColor: 'rgba(27, 79, 216, 0.5)',
      color: '#111118'
    },
    '.Tab--selected': {
      backgroundColor: 'rgba(27, 79, 216, 0.08)',
      borderColor: '#1B4FD8',
      color: '#1B4FD8'
    },
    '.TabIcon--selected': {
      fill: '#1B4FD8'
    },
    '.Error': {
      color: '#DC2626',
      fontSize: '12px'
    }
  }
};

// Payment form component
const CheckoutForm = ({ orderId, amount, onSuccess, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const paymentInfoTracked = useRef(false);

  // Track AddPaymentInfo when user starts interacting with payment form
  const handlePaymentElementChange = (event) => {
    // Track only once when user first interacts with the form
    if (!paymentInfoTracked.current && !event.empty) {
      paymentInfoTracked.current = true;
      trackAddPaymentInfo({
        orderId: orderId,
        value: amount,
        paymentMethodType: 'card',
        numItems: 1
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setErrorMessage(null);

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/checkout/success`,
        },
        redirect: 'if_required'
      });

      if (error) {
        setErrorMessage(error.message);
        onError?.(error.message);
      } else if (paymentIntent) {
        if (paymentIntent.status === 'succeeded') {
          // Confirm with our backend
          const confirmResponse = await fetch(
            `${API_URL}/api/payments/payment-intent/confirm/${paymentIntent.id}`,
            { method: 'POST' }
          );
          
          if (confirmResponse.ok) {
            const confirmData = await confirmResponse.json();
            onSuccess?.(confirmData);
          } else {
            onSuccess?.({ payment_intent_id: paymentIntent.id, status: 'succeeded' });
          }
        } else if (paymentIntent.status === 'requires_action') {
          // OXXO voucher generated
          const confirmResponse = await fetch(
            `${API_URL}/api/payments/payment-intent/confirm/${paymentIntent.id}`,
            { method: 'POST' }
          );
          
          if (confirmResponse.ok) {
            const confirmData = await confirmResponse.json();
            onSuccess?.(confirmData);
          }
        } else {
          setErrorMessage('El pago no pudo ser procesado. Por favor intenta de nuevo.');
        }
      }
    } catch (err) {
      console.error('Payment error:', err);
      setErrorMessage('Error al procesar el pago. Por favor intenta de nuevo.');
      onError?.(err.message);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <PaymentElement 
        onChange={handlePaymentElementChange}
        options={{
          layout: 'tabs',
          paymentMethodOrder: ['card'],
          defaultValues: {
            billingDetails: {
              address: {
                country: 'MX'
              }
            }
          }
        }}
      />
      
      {errorMessage && (
        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-300 rounded-lg">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-600">{errorMessage}</p>
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || processing}
        className={`
          w-full py-4 px-6 rounded-lg font-medium text-base
          flex items-center justify-center gap-2
          transition-all duration-300 text-white
          ${processing
            ? 'bg-brand-cobalt/50 cursor-not-allowed'
            : 'bg-brand-cobalt hover:opacity-90 hover:shadow-brand-cobalt'
          }
        `}
        data-testid="pay-now-btn"
      >
        {processing ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Procesando pago...
          </>
        ) : (
          <>
            <CreditCard className="w-5 h-5" />
            Pagar {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(amount)}
          </>
        )}
      </button>

      {/* Security badges */}
      <div className="flex items-center justify-center gap-4 pt-2">
        <div className="flex items-center gap-1.5 text-brand-black/40 text-xs">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>
          </svg>
          <span>Pago seguro</span>
        </div>
        <div className="flex items-center gap-1.5 text-brand-black/40 text-xs">
          <span>Procesado por</span>
          <svg className="h-4" viewBox="0 0 60 25" fill="currentColor">
            <path d="M59.64 14.28h-8.06c.19 1.93 1.6 2.55 3.2 2.55 1.64 0 2.96-.37 4.05-.95v3.32a8.33 8.33 0 0 1-4.56 1.1c-4.01 0-6.83-2.5-6.83-7.48 0-4.19 2.39-7.52 6.3-7.52 3.92 0 5.96 3.28 5.96 7.5 0 .4-.02 1.04-.06 1.48zm-3.67-3.12c0-1.5-.65-2.67-1.97-2.67-1.1 0-2.01.85-2.28 2.67h4.25zM40.95 5.57v14.77h4.09V6.72h5.18V5.57H40.95zm-7.85 1.15V5.57h-5.8v14.77h4.09v-5.77h4.83v-1.15h-4.83v-6.7h5.71zm-13.03 0V5.57H8.3v14.77h11.77v-1.15H12.39v-6.7h5.71V11.34H12.39v-4.62h7.68zm-19.9 0V5.57H0v14.77h4.09V8.87l4.15 11.47h2.93l4.15-11.47v11.47h4.09V5.57h-5.8l-3.72 10.32L6.17 5.57H.17z"/>
          </svg>
        </div>
      </div>
    </form>
  );
};

// Main embedded checkout component
const StripeEmbeddedCheckout = ({ 
  orderId, 
  amount,
  paymentMethodType = 'card',
  onSuccess,
  onError,
  onCancel
}) => {
  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const initializePayment = async () => {
      try {
        setLoading(true);
        setError(null);

        // Create payment intent
        const response = await fetch(`${API_URL}/api/payments/payment-intent/create`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            order_id: orderId,
            payment_method_type: paymentMethodType
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Error al inicializar el pago');
        }

        const data = await response.json();
        
        // Load Stripe with publishable key
        const stripe = await loadStripe(data.publishable_key);
        setStripePromise(stripe);
        setClientSecret(data.client_secret);

      } catch (err) {
        console.error('Payment initialization error:', err);
        setError(err.message);
        onError?.(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (orderId && amount > 0) {
      initializePayment();
    }
  }, [orderId, amount, paymentMethodType]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-brand-cobalt animate-spin mb-4" />
        <p className="text-brand-black/60 text-sm">Preparando formulario de pago...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-red-50 border border-red-300 rounded-lg">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
          <div>
            <h3 className="font-medium text-red-600 mb-1">Error al cargar el pago</h3>
            <p className="text-sm text-red-500">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-3 text-sm text-brand-cobalt hover:underline"
            >
              Intentar de nuevo
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!stripePromise || !clientSecret) {
    return null;
  }

  return (
    <Elements 
      stripe={stripePromise} 
      options={{
        clientSecret,
        appearance: stripeAppearance,
        locale: 'es-419'
      }}
    >
      <CheckoutForm 
        orderId={orderId}
        amount={amount}
        onSuccess={onSuccess}
        onError={onError}
      />
    </Elements>
  );
};

export default StripeEmbeddedCheckout;
